import { Badge } from "@/components/ui/badge";
import { Building2, Phone, Wrench, PoundSterling } from "lucide-react";
import { cn } from "@/lib/utils";

const priorityColors = {
  high: "bg-red-500/10 text-red-400 border-red-500/20",
  medium: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  low: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
};

export const LeadCard = ({ lead, onClick, provided, isDragging, hasOverdue }) => {
  return (
    <div
      ref={provided?.innerRef}
      {...(provided?.draggableProps || {})}
      {...(provided?.dragHandleProps || {})}
      data-testid={`lead-card-${lead.id}`}
      onClick={() => onClick?.(lead)}
      className={cn(
        "relative bg-card border border-white/5 rounded-sm p-3.5 cursor-pointer group",
        "hover:bg-white/[0.04] hover:border-white/10 transition-colors duration-150",
        isDragging && "kanban-card-dragging border-blue-500/30 bg-card"
      )}
    >
      {hasOverdue && (
        <div
          data-testid={`lead-card-overdue-${lead.id}`}
          className="absolute top-2 right-2 w-2 h-2 rounded-full bg-red-500 ring-2 ring-card"
          title="Has overdue activity"
        />
      )}
      <div className="flex items-start justify-between mb-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" strokeWidth={1.5} />
          <span className="text-sm font-semibold text-foreground truncate">
            {lead.company_name}
          </span>
        </div>
        <Badge
          variant="outline"
          className={cn("text-[10px] px-1.5 py-0 flex-shrink-0", priorityColors[lead.priority] || priorityColors.medium)}
        >
          {lead.priority}
        </Badge>
      </div>

      <p className="text-xs text-muted-foreground mb-3 truncate">
        {lead.contact_name}
      </p>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <Wrench className="w-3 h-3 text-muted-foreground" strokeWidth={1.5} />
          <span className="text-[10px] text-muted-foreground truncate max-w-[100px]">{lead.service_type}</span>
        </div>
        <div className="flex items-center gap-1">
          <PoundSterling className="w-3 h-3 text-amber-400" strokeWidth={1.5} />
          <span className="text-xs font-semibold text-amber-400">
            {Number(lead.estimated_value).toLocaleString()}
          </span>
        </div>
      </div>

      {lead.phone && (
        <a
          href={`tel:${lead.phone}`}
          onClick={e => e.stopPropagation()}
          className="flex items-center gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 text-blue-400 hover:text-blue-300"
          data-testid={`lead-card-phone-${lead.id}`}
        >
          <Phone className="w-3 h-3" strokeWidth={1.5} />
          <span className="text-[10px]">{lead.phone}</span>
        </a>
      )}
    </div>
  );
};
